import type { JSX } from 'react';
import { generatePath } from 'react-router';

import { Link } from 'Components/Buttons/Buttons';
import { CollapsibleModal } from 'Components/Modal/CollapsibleModal/CollapsibleModal';
import { Text } from 'Components/Text/Text';
import { ACTION_DETAILS } from 'Config/PathTemplates';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { Action } from 'Models/Actions';

import styles from './RiskRegisterDashboard.module.css';

export enum ActionModalDisplayType {
    OPEN = 'Open Actions',
    CLOSED = 'Closed Actions',
}

export interface ActionDisplayModalProps {
    hideModal: () => void;
    selectedActions: Action[];
    date: string;
    modalType: ActionModalDisplayType;
}

/**
 * For a specific month, displays one of the following (based on where the user clicked in the parent chart):
 *   The number of Actions opened
 *   The number of Actions closed
 *   The total number of Actions in the system
 */
export const ActionDisplayModal = (props: ActionDisplayModalProps): JSX.Element => {
    const displayActionDate = (action: Action) => {
        switch (props.modalType) {
            case ActionModalDisplayType.CLOSED:
                return action.closed_timestamp ? `Closed ${iso8601ToUsDateShort(action.closed_timestamp)}` : '';
            case ActionModalDisplayType.OPEN:
                return action.due_date ? `Due ${iso8601ToUsDateShort(action.due_date)}` : action.created_timestamp ? `Created ${iso8601ToUsDateShort(action.created_timestamp)}` : '';
        }
    };

    const sortActionsByDateType = (actionA: Action, actionB: Action): number => {
        if (props.modalType === ActionModalDisplayType.CLOSED) {
            if (actionA.closed_timestamp! > actionB.closed_timestamp!) {
                return 1;
            } else if (actionA.closed_timestamp! < actionB.closed_timestamp!) {
                return -1;
            } else {
                return actionA.title.localeCompare(actionB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        } else {
            if (actionA.created_timestamp! > actionB.created_timestamp!) {
                return 1;
            } else if (actionA.created_timestamp! < actionB.created_timestamp!) {
                return -1;
            } else {
                return actionA.title.localeCompare(actionB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        }
    };

    const closedActionListing = props.selectedActions.sort(sortActionsByDateType).map((action) => (
        <div className={styles.modalRow} key={action.id}>
            <Link to={generatePath(ACTION_DETAILS, { action_id: action.id })} size="sm" openInNewTab>
                {action.title}
            </Link>
            <div className={styles.modalRowSeparator}>
                <Text color="darkGray" noStyles={true}>
                    {displayActionDate(action)}
                </Text>
            </div>
        </div>
    ));

    return <CollapsibleModal hideModal={props.hideModal} displayItems={closedActionListing} headerText={{ text: props.modalType, secondaryText: `${props.date}` }} />;
};
